import React, { useEffect, useState } from "react";
import PaymentsIcon from "@mui/icons-material/Payments";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { createData, deleteData } from "../../utility/adminApi";
import ConfirmModal from "../../components/ConfirmModal";
import loader from "../../assets/loader.webp";
import AddPaymentMethod from "./AddPaymentMethod";
import EditPaymentMethod from "./EditPaymentMethod";
import axios from "axios";
import { notify } from "../../components/toastify";

function PaymentMethod() {
  const [payMethodData, setPayMethodData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [addtab, setAddtab] = useState(false);
  const [editTab, setEdittab] = useState(false);
  const [formData, setFormData] = useState({});
  const [singleData, setSingleData] = useState({});
  const [methodIdToDelete, setMethodIdToDelete] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Account Details States
  const [accountModal, setAccountModal] = useState(false);
  const [accountNumber, setAccountNumber] = useState("");
  const [selectedBank, setSelectedBank] = useState(""); // bank code
  const [selectedBankName, setSelectedBankName] = useState(""); // bank name
  const [accountName, setAccountName] = useState("");
  const [validateLoading, setValidateLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [bankFetchLoading, setBankFetchLoading] = useState(false);
  const [accountFeedback, setAccountFeedback] = useState("");
  const [createAccLoading, setCreateAccLoading] = useState(false);
  const [subAccountDetails, setSubAccountDetails] = useState(null); // This is the .data from API or null

  // For searchable dropdown
  const [bankSearch, setBankSearch] = useState("");

  // Helper: API base
  const API_BASE = "https://01pos2.01technologies.net/api/admin";
  // Helper: add admin token for protected endpoints
  const getHeaders = () => {
    const token = sessionStorage.getItem("admin_token");
    return token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};
  };

  const fetchPaymentMethods = async () => {
    const response = await createData("payment-methods", {});
    setPayMethodData(response?.data);
    setIsLoading(false);
  };

  // Fetch all banks for the select dropdown
  const fetchBanks = async () => {
    setBankFetchLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/subaccount/banks`, {
        headers: getHeaders(),
      });
      setBanks(res?.data?.data || []);
    } catch (err) {
      setBanks([]);
    } finally {
      setBankFetchLoading(false);
    }
  };

  // Call this if you want to display account details if available
  const fetchSubAccountDetails = async () => {
    try {
      const res = await axios.get(`${API_BASE}/subaccount/details`, {
        headers: getHeaders(),
      });
      // Only set subAccountDetails if has_subaccount === true
      if (res?.data?.data && res.data.data.has_subaccount) {
        setSubAccountDetails(res.data.data);
      } else {
        setSubAccountDetails(null);
      }
    } catch (error) {
      setSubAccountDetails(null);
    }
  };

  const handleAdd = () => {
    setAddtab(true);
    setEdittab(false);
  };

  const handleEditFile = async (item) => {
    setSingleData(item);
    setFormData(item);
    setEdittab(true);
  };

  const handleDelete = (id) => {
    setMethodIdToDelete(id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    await deleteData(
      "payment-methods/delete/",
      methodIdToDelete
    );
    fetchPaymentMethods();
    setIsModalOpen(false);
  };

  useEffect(() => {
    fetchPaymentMethods();
    fetchSubAccountDetails();
    // eslint-disable-next-line
  }, []);

  // --- Account Details Modal Logic ---
  const openAccountModal = () => {
    setAccountModal(true);
    setAccountNumber("");
    setSelectedBank("");
    setSelectedBankName("");
    setAccountName("");
    setAccountFeedback("");
    setBankSearch("");
    fetchBanks();
  };

  const closeAccountModal = () => {
    setAccountModal(false);
    setAccountNumber("");
    setSelectedBank("");
    setSelectedBankName("");
    setAccountName("");
    setAccountFeedback("");
    setBankSearch("");
  };

  const handleValidateAccount = async (e) => {
    e.preventDefault();
    setAccountFeedback("");
    setValidateLoading(true);
    if (!accountNumber.match(/^\d{10}$/)) {
      setAccountFeedback("Account number must be 10 digits");
      setValidateLoading(false);
      return;
    }
    if (!selectedBank) {
      setAccountFeedback("Select a bank");
      setValidateLoading(false);
      return;
    }

    try {
      const res = await axios.post(
        `${API_BASE}/subaccount/resolve-account`,
        {
          account_number: accountNumber,
          bank_code: selectedBank,
        },
        { headers: getHeaders() }
      );

      const fetchedName = res?.data?.data?.account_name || "";
      if (fetchedName) {
        setAccountName(fetchedName);
        setAccountFeedback("Account resolved successfully");
      } else {
        setAccountFeedback("Unable to resolve account. Try again.");
        setAccountName("");
      }
    } catch (error) {
      setAccountFeedback(
        error?.response?.data?.message ||
          "Error resolving account. Please check details."
      );
      setAccountName("");
    } finally {
      setValidateLoading(false);
    }
  };

  const handleCreateSubAccount = async () => {
    setCreateAccLoading(true);
    setAccountFeedback("");
    try {
      // Get bank name by code
      let bankName = "";
      if (selectedBankName) {
        bankName = selectedBankName;
      } else {
        const found = banks.find((b) => b.code === selectedBank);
        bankName = found ? found.name : "";
      }

      await axios.post(
        `${API_BASE}/subaccount/create`,
        {
          account_number: accountNumber,
          bank_code: selectedBank,
          bank_name: bankName,
          account_name: accountName,
        },
        { headers: getHeaders() }
      );
      setAccountFeedback("Bank account added successfully.");
      fetchSubAccountDetails();
      notify("Bank account added successfully.");
      setTimeout(() => {
        closeAccountModal();
        setCreateAccLoading(false);
      }, 1200);
    } catch (error) {
      setAccountFeedback(
        error?.response?.data?.message ||
          "Error creating account. Try again."
      );
      setCreateAccLoading(false);
    }
  };

  // Filter banks by bankSearch string (case-insensitive)
  const filteredBanks = bankSearch
    ? banks.filter((bank) =>
        bank.name.toLowerCase().includes(bankSearch.toLowerCase())
      )
    : banks;

  // ---- RENDER ----
  return (
    <div className="lg:overflow-y-hidden">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-center p-4 w-full">
        <h3 className="text-xl font-semibold flex items-center gap-2">
          <PaymentsIcon />
          Payment Method
        </h3>
        <div className="flex gap-6">
          <button
            onClick={() => {
              handleAdd();
            }}
            className="p-3 rounded-lg text-white bg-blue-500"
          >
            Add New Payment
          </button>
          <button
            onClick={openAccountModal}
            className="p-3 rounded-lg text-white bg-green-500"
          >
            Manage Bank Account
          </button>
        </div>
      </div>

      {/* Account Details Section */}
      <div className="py-2 px-4">
        <div className="border bg-gray-50 rounded-md p-4 mb-2">
          <h4 className="font-semibold text-lg mb-2">Bank Account Details</h4>
          {subAccountDetails ? (
            <div className="flex flex-col gap-2">
              <div>
                <span className="font-bold">Account Name:</span>{" "}
                {subAccountDetails.account_name}
              </div>
              <div>
                <span className="font-bold">Account Number:</span>{" "}
                {subAccountDetails.account_number}
              </div>
              <div>
                <span className="font-bold">Bank:</span>{" "}
                {subAccountDetails.bank_name}
              </div>
            </div>
          ) : (
            <span className="text-gray-500">
              No bank account added yet. Click "Add/Update Bank Account".
            </span>
          )}
        </div>
      </div>

      {/* --- Modal for Adding Account Details --- */}
      {accountModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-4 rounded-md shadow-lg min-w-[320px] max-w-full w-full md:w-[400px]">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-bold text-lg"> Add Bank Account </h4>
              <button
                className="text-red-500 font-bold text-xl px-2"
                onClick={closeAccountModal}
                disabled={validateLoading || createAccLoading}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleValidateAccount} className="flex flex-col gap-3">
              <div>
                <label className="block text-gray-700">Account Number</label>
                <input
                  className="border p-2 rounded w-full"
                  maxLength={10}
                  minLength={10}
                  value={accountNumber}
                  onChange={(e) => {
                    setAccountNumber(e.target.value.replace(/\D/g, ""));
                    setAccountName("");
                  }}
                  required
                  placeholder="Enter 10-digit account number"
                  disabled={validateLoading || createAccLoading}
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-1">Bank</label>
                {bankFetchLoading ? (
                  <span className="text-sm text-gray-500">Fetching banks...</span>
                ) : (
                  <div>
                    <input
                      className="border p-2 rounded w-full mb-1"
                      placeholder="Type to search banks..."
                      value={bankSearch}
                      onChange={(e) => {
                        setBankSearch(e.target.value);
                      }}
                      disabled={validateLoading || createAccLoading || !banks.length}
                    />
                    <select
                      className="border p-2 rounded w-full"
                      value={selectedBank}
                      disabled={validateLoading || createAccLoading || !filteredBanks.length}
                      onChange={(e) => {
                        setSelectedBank(e.target.value);
                        // Get the selected bank name
                        const sel = banks.find((b) => b.code === e.target.value);
                        setSelectedBankName(sel ? sel.name : "");
                        setAccountName("");
                      }}
                      required
                    >
                      <option value="" disabled>
                        {filteredBanks.length === 0 ? "No banks found" : "Select Bank"}
                      </option>
                      {filteredBanks.map((bank) => (
                        <option key={bank.code} value={bank.code}>
                          {bank.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {/* Validate & Fetch Account Name */}
              <button
                type="submit"
                className="bg-blue-500 text-white p-2 rounded w-full mt-2"
                disabled={
                  !accountNumber ||
                  !selectedBank ||
                  validateLoading ||
                  createAccLoading
                }
              >
                {validateLoading ? "Validating..." : "Validate Account"}
              </button>
              {accountName && (
                <div>
                  <label className="block mb-1 text-sm text-gray-700">Account Name</label>
                  <input
                    className="border p-2 rounded w-full bg-gray-100"
                    value={accountName}
                    disabled
                  />
                </div>
              )}
              {accountFeedback && (
                <div
                  className={
                    accountFeedback.toLowerCase().includes("success")
                      ? "text-green-700 text-sm"
                      : "text-red-600 text-sm"
                  }
                >
                  {accountFeedback}
                </div>
              )}
              {accountName && (
                <button
                  type="button"
                  className="bg-green-600 text-white p-2 rounded w-full mt-2"
                  onClick={handleCreateSubAccount}
                  disabled={createAccLoading}
                >
                  {createAccLoading ? "Saving..." : "Save Bank Account"}
                </button>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Existing add/edit payment logic */}
      {addtab && !editTab && (
        <AddPaymentMethod
          setIsLoading={setIsLoading}
          setAddtab={setAddtab}
          fetchPaymentMethods={fetchPaymentMethods}
        />
      )}
      {editTab && !addtab && (
        <EditPaymentMethod
          singleData={singleData}
          setSingleData={setSingleData}
          setEdittab={setEdittab}
          formData={formData}
          setFormData={setFormData}
          fetchPaymentMethods={fetchPaymentMethods}
        />
      )}
      {!editTab && !addtab && (
        <div className="w-full p-4 flex flex-col gap-2  bg-white">
          <div className="w-full">
            {isLoading ? (
              <div className="flex justify-center items-center">
                <img
                  src={loader}
                  alt="loader"
                  className="w-10 lg:w-20"
                />
              </div>
            ) : (
              <table className="min-w-full bg-white border border-gray-300">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="py-2 px-4 border-b border-gray-300">METHOD</th>
                    <th className="py-2 px-4 border-b border-gray-300 text-wrap">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {payMethodData?.map((item) => (
                    <tr
                      key={item?.id}
                      className="hover:bg-gray-100 border-b border-gray-300"
                    >
                      <td className="py-2 px-4 text-wrap text-center">
                        {item?.method_name}
                      </td>
                      <td className="py-2 flex justify-center gap-3 px-2 ">
                        <EditIcon
                          onClick={() => handleEditFile(item)}
                          className="text-blue-500 cursor-pointer"
                        />
                        <DeleteIcon
                          onClick={() => handleDelete(item?.id)}
                          className="text-red-500 cursor-pointer"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        text1="Confirm Delete"
        text2="Are you sure you want to delete the bill?"
      />
    </div>
  );
}

export default PaymentMethod;
